<template>
  <div class="parser-manager">
    <!-- 顶部操作栏 -->
    <div class="top-bar">
      <div class="left">
        <div class="search-input">
          <Input
            v-model="searchText"
            placeholder="搜索提示词名称或内容..."
            icon="md-search"
            clearable
            @on-change="handleSearch"
          />
        </div>
                <div class="filter-options">
          <Select v-model="filterType" placeholder="筛选类型" style="width: 120px">
            <Option value="">全部</Option>
            <Option value="my">我的</Option>
            <Option value="shared">共享</Option>
          </Select>
        </div>
      </div>
      
      <!-- 部门筛选标签 -->
        <div class="filter-tags">
          <Tag 
            :color="filterDep === '' ? 'primary' : 'default'"
            @click.native="filterByDepartment('')"
            class="filter-tag"
          >
            全部 ({{ totalCount }})
          </Tag>
          <Tag 
            :color="filterDep === 'null' ? 'primary' : 'default'"
            @click.native="filterByDepartment('null')"
            class="filter-tag"
          >
            通用 ({{ nullDepCount }})
          </Tag>
          <Tag 
            v-for="dep in departmentStats" 
            :key="dep.id"
            :color="filterDep === dep.id ? 'primary' : 'default'"
            @click.native="filterByDepartment(dep.id)"
            class="filter-tag"
          >
            {{ dep.name }} ({{ dep.count }})
          </Tag>
        </div>


      <div class="right">
        <Button type="primary" icon="md-add" @click="showCreateModal = true">
          新建提示词
        </Button>
      </div>
    </div>

    <!-- 提示词列表 -->
    <div class="parser-list">
      <div v-if="loading" class="loading-container">
        <Spin size="large" />
        <p>加载中...</p>
      </div>

      <div v-else-if="filteredItems.length === 0" class="empty-container">
        <Icon type="md-document" size="64" color="#ccc" />
        <p>暂无提示词</p>
        <Button type="primary" @click="showCreateModal = true">创建第一个提示词</Button>
      </div>

      <div v-else class="items-grid">
        <div
          v-for="item in filteredItems"
          :key="item.id"
          class="parser-item"
          :class="{ 'shared': item.shared }"
          @click="openDetailModal(item)"
        >
          <div class="item-header">
            <div class="item-title">
              <h3>{{ item.name }}</h3>
              <Tag v-if="item.shared" color="green" size="small">共享</Tag>
            </div>
            <div class="item-actions" @click.stop>
              <Button
                type="text"
                icon="md-copy"
                size="small"
                @click="copyContent(item.content)"
                title="复制内容"
              ></Button>
              <Dropdown trigger="click" @on-click="handleAction($event, item)">
                <Button type="text" icon="md-more" size="small"></Button>
                <DropdownMenu slot="list">
                  <DropdownItem name="edit">编辑</DropdownItem>
                  <DropdownItem
                    v-if="canDelete(item)"
                    name="delete"
                  >删除</DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>

          <div class="item-content">
            <p>{{ getContentSummary(item.content) }}</p>
            <div v-if="item.demo" class="item-demo">
              <p class="demo-label">应用方法：</p>
              <p>{{ getContentSummary(item.demo) }}</p>
            </div>
          </div>

          <div class="item-footer">
            <div class="item-meta">
              <BaseNameBoard :size="16" :uid="item.created_by" />
              <span class="created-time">{{ formatTime(item.created_at) }}</span>
            </div>
            <div v-if="item.dep_id" class="item-department">
              <Tag color="blue" size="small">{{ getDepartmentName(item.dep_id) }}</Tag>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 创建/编辑模态框 -->
    <Modal
      v-model="showCreateModal"
      :title="editingItem ? '编辑提示词' : '新建提示词'"
      width="600"
      :mask-closable="false"
    >
      <Form ref="form" :model="formData" :rules="formRules" label-position="top">
        <FormItem label="提示词名称" prop="name">
          <Input
            v-model="formData.name"
            placeholder="为您的提示词取一个名字"
            maxlength="50"
            show-word-limit
          />
        </FormItem>

        <FormItem label="提示词内容" prop="content">
          <Input
            v-model="formData.content"
            type="textarea"
            :rows="8"
            placeholder="请输入提示词内容..."
            maxlength="2000"
            show-word-limit
          />
        </FormItem>

        <FormItem label="所属部门" prop="dep_id">
          <BaseDepSelectPro v-model="formData.dep_id" placeholder="请选择部门" />
        </FormItem>

        <FormItem label="AI应用选择及应用方法介绍" prop="demo">
          <Input
            v-model="formData.demo"
            type="textarea"
            :rows="4"
            placeholder="请介绍该提示词的使用方法和应用场景..."
            maxlength="1000"
            show-word-limit
          />
        </FormItem>

        <FormItem label="共享设置">
          <Checkbox v-model="formData.shared">
            共享给其他用户
          </Checkbox>
          <p class="form-tip">共享的提示词可以被其他用户查看和使用</p>
        </FormItem>
      </Form>

      <div slot="footer">
        <Button @click="showCreateModal = false">取消</Button>
        <Button
          type="primary"
          :loading="submitting"
          @click="handleSubmit"
        >
          {{ editingItem ? '保存修改' : '创建提示词' }}
        </Button>
      </div>
    </Modal>

    <!-- 详情展示模态框 -->
    <Modal v-model="showDetailModal" :title="detailItem ? detailItem.name : '提示词详情'" width="800" :mask-closable="true">
      <div v-if="detailItem" class="detail-content">
        <div class="detail-header">
          <div class="detail-title">
            <h2>{{ detailItem.name }}</h2>
            <div class="detail-meta">
              <Tag v-if="detailItem.shared" color="green" size="small">共享</Tag>
              <Tag v-if="detailItem.dep_id" color="blue" size="small">{{ getDepartmentName(detailItem.dep_id) }}</Tag>
            </div>
          </div>
          <div class="detail-actions">
            <Button type="primary" icon="md-copy" @click="copyContent(detailItem.content)">
              复制提示词
            </Button>
          </div>
        </div>

        <div class="detail-section">
          <h3>应用方法介绍</h3>
          <div v-if="detailItem.demo" class="demo-content">
            <div class="demo-text">{{ detailItem.demo }}</div>
          </div>
          <div v-else class="no-demo">
            <p>暂无应用方法介绍</p>
          </div>
        </div>

        <div class="detail-section">
          <h3>提示词内容</h3>
          <div class="content-preview">
            <pre>{{ detailItem.content }}</pre>
          </div>
        </div>

        <div class="detail-footer">
          <div class="detail-info">
            <span>创建者：<BaseNameBoard :size="16" :uid="detailItem.created_by" /></span>
            <span>创建时间：{{ formatTime(detailItem.created_at) }}</span>
          </div>
        </div>
      </div>
    </Modal>

    <!-- 确认删除模态框 -->
    <Modal v-model="showDeleteModal" title="确认删除" width="400">
      <p>确定要删除提示词 "{{ deletingItem && deletingItem.name }}" 吗？</p>
      <p style="color: #999; font-size: 12px;">此操作不可恢复</p>

      <div slot="footer">
        <Button @click="showDeleteModal = false">取消</Button>
        <Button
          type="error"
          :loading="deleting"
          @click="confirmDelete"
        >
          确认删除
        </Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import moment from 'moment';

export default {
  name: 'ParserManager',
  data() {
    return {
      loading: false,
      submitting: false,
      deleting: false,
      items: [],
      searchText: '',
      filterType: '',
      filterDep: '',
      showCreateModal: false,
      showDeleteModal: false,
      showDetailModal: false,
      editingItem: null,
      deletingItem: null,
      detailItem: null,
      formData: {
        name: '',
        content: '',
        dep_id: '',
        demo: '',
        shared: false,
      },
      formRules: {
        name: [
          {required: true, message: '请输入提示词名称', trigger: 'blur'},
          {min: 1, max: 50, message: '名称长度在 1 到 50 个字符', trigger: 'blur'},
        ],
        content: [
          {required: true, message: '请输入提示词内容', trigger: 'blur'},
          {min: 1, max: 2000, message: '内容长度在 1 到 2000 个字符', trigger: 'blur'},
        ],
        dep_id: [
          {required: true, message: '请选择所属部门', trigger: 'change'},
        ],
        demo: [
          {required: false, message: '请输入应用方法介绍', trigger: 'blur'},
          {max: 1000, message: '介绍长度不能超过 1000 个字符', trigger: 'blur'},
        ],
      },
    };
  },
  computed: {
    ...mapGetters('session', ['session']),

    filteredItems() {
      let items = this.items;

      // 搜索过滤
      if (this.searchText) {
        const searchLower = this.searchText.toLowerCase();
        items = items.filter((item) =>
          item.name.toLowerCase().includes(searchLower) ||
          item.content.toLowerCase().includes(searchLower) ||
          (item.demo && item.demo.toLowerCase().includes(searchLower)),
        );
      }

      // 类型过滤
      if (this.filterType === 'my') {
        items = items.filter((item) => item.created_by === this.session.id);
      } else if (this.filterType === 'shared') {
        items = items.filter((item) => item.shared);
      }

      // 部门过滤
      if (this.filterDep) {
        if (this.filterDep === 'null') {
          items = items.filter((item) => !item.dep_id || item.dep_id === null);
        } else {
          items = items.filter((item) => item.dep_id === this.filterDep);
        }
      }

      return items;
    },

    currentUserId() {
      return this.session && this.session.id;
    },

    isAdmin() {
      return (this.session && this.session.role === 'admin') || (this.session && this.session.is_admin);
    },

    departments() {
      const deps = this.$store.getters['session/deps'];
      return deps ? deps.filter(dep => dep.ignore !== 1) : [];
    },

    // 部门统计
    departmentStats() {
      const deps = this.departments;
      const stats = [];
      
      deps.forEach(dep => {
        const count = this.items.filter(item => item.dep_id === dep.id).length;
        if (count > 0) {
          stats.push({
            id: dep.id,
            name: dep.name,
            count: count
          });
        }
      });
      
      return stats.sort((a, b) => b.count - a.count); // 按数量降序排列
    },

    // 总数统计
    totalCount() {
      return this.items.length;
    },

    // 通用（无部门）统计
    nullDepCount() {
      return this.items.filter(item => !item.dep_id || item.dep_id === null).length;
    },
  },

  mounted() {
    this.loadData();
  },

  methods: {
    // 加载数据
    async loadData() {
      this.loading = true;
      try {
        const res = await this.$api.get('/ai/parse?q=all');
        this.items = res.data.data || [];
      } catch (error) {
        this.Error('加载提示词列表失败');
        console.error('Load parsers error:', error);
      } finally {
        this.loading = false;
      }
    },

    // 搜索处理
    handleSearch() {
      // 实时搜索，不需要额外处理
    },

    // 部门筛选处理
    filterByDepartment(depId) {
      this.filterDep = depId;
    },

    // 显示详情模态框
    openDetailModal(item) {
      this.detailItem = item;
      this.showDetailModal = true;
    },

    // 获取内容摘要
    getContentSummary(content) {
      if (!content) return '';
      const summary = content.replace(/\n/g, ' ').trim();
      return summary.length > 30 ? summary.substring(0, 30) + '...' : summary;
    },

    // 格式化时间
    formatTime(time) {
      if (!time) return '';
      return moment(time).format('YYYY-MM-DD HH:mm');
    },

    // 获取部门名称
    getDepartmentName(depId) {
      if (!depId) return '';
      const deps = this.$store.getters['session/deps'];
      if (!deps) return '';
      const dep = deps.find(d => d.id === depId);
      return dep ? dep.name : '';
    },

    // 检查是否可以删除
    canDelete(item) {
      return this.isAdmin || item.created_by === this.currentUserId;
    },

    // 处理操作
    handleAction(action, item) {
      switch (action) {
        case 'edit':
          this.editItem(item);
          break;
        case 'delete':
          this.deleteItem(item);
          break;
      }
    },

    // 编辑项目
    editItem(item) {
      this.editingItem = item;
      this.formData = {
        name: item.name,
        content: item.content,
        dep_id: item.dep_id || '',
        demo: item.demo || '',
        shared: item.shared,
      };
      this.showCreateModal = true;
    },

    // 复制内容
    copyContent(content) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(content).then(() => {
          this.Success('内容已复制到剪贴板');
        }).catch(() => {
          this.fallbackCopy(content);
        });
      } else {
        this.fallbackCopy(content);
      }
    },

    // 备用复制方法
    fallbackCopy(text) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        this.Success('内容已复制到剪贴板');
      } catch (err) {
        this.Error('复制失败');
      }
      document.body.removeChild(textArea);
    },

    // 删除项目
    deleteItem(item) {
      this.deletingItem = item;
      this.showDeleteModal = true;
    },

    // 确认删除
    async confirmDelete() {
      if (!this.deletingItem) return;

      this.deleting = true;
      try {
        await this.$api.delete(`/ai/parse/${this.deletingItem.id}`);
        this.Success('删除成功');
        this.showDeleteModal = false;
        this.deletingItem = null;
        this.loadData();
      } catch (error) {
        this.Error('删除失败');
        console.error('Delete parser error:', error);
      } finally {
        this.deleting = false;
      }
    },

    // 提交表单
    async handleSubmit() {
      const valid = await this.$refs.form.validate();
      if (!valid) return;

      this.submitting = true;
      try {
        if (this.editingItem) {
          // 更新
          await this.$api.put(`/ai/parse/${this.editingItem.id}`, this.formData);
          this.Success('更新成功');
        } else {
          // 创建
          await this.$api.post('/ai/parse', this.formData);
          this.Success('创建成功');
        }

        this.showCreateModal = false;
        this.resetForm();
        this.loadData();
      } catch (error) {
        this.Error(this.editingItem ? '更新失败' : '创建失败');
        console.error('Submit parser error:', error);
      } finally {
        this.submitting = false;
      }
    },

    // 重置表单
    resetForm() {
      this.editingItem = null;
      this.formData = {
        name: '',
        content: '',
        dep_id: '',
        demo: '',
        shared: false,
      };
      this.$refs.form.resetFields();
    },
  },
};
</script>

<style lang="less" scoped>
.parser-manager {
  padding: 20px;
  background: var(--bg2);
  min-height: 100vh;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  .left {
    display: flex;
    align-items: center;
    gap: 16px;

    .search-input {
      width: 300px;
    }

    .filter-options {
      flex-shrink: 0;
    }
  }
}

.department-filter {
  margin-bottom: 24px;
  padding: 16px;
  background: var(--bg2);
  border-radius: 8px;
  border: 1px solid var(--border);

  .filter-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    .filter-tag {
      cursor: pointer;
      transition: all 0.3s ease;
      font-size: 13px;
      padding: 4px 12px;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }
    }
  }
}

.loading-container,
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--subtext2);

  p {
    margin: 16px 0 0 0;
    font-size: 16px;
  }
}

.items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
}

.parser-item {
  background: var(--bg2);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 16px;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: var(--primary);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  &.shared {
    border-color: #52c41a;
  }
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;

  .item-title {
    flex: 1;
    margin-right: 12px;

    h3 {
      margin: 0 0 4px 0;
      font-size: 16px;
      font-weight: 600;
      color: var(--text2);
      line-height: 1.4;
    }
  }

  .item-actions {
    display: flex;
    align-items: center;
    gap: 4px;

    .ivu-btn {
      padding: 4px 6px;
      border-radius: 4px;

      &:hover {
        background: var(--bg3);
        color: var(--primary);
      }
    }
  }
}

.item-content {
  margin-bottom: 16px;

  p {
    margin: 0;
    color: var(--text2);
    font-size: 14px;
    line-height: 1.5;
    word-break: break-word;
  }

  .item-demo {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--border);

    .demo-label {
      font-size: 12px;
      color: var(--subtext2);
      margin-bottom: 4px;
    }

    p {
      font-size: 13px;
      color: var(--text2);
    }
  }
}

.item-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .item-meta {
    display: flex;
    align-items: center;
    gap: 8px;

    .created-time {
      color: var(--subtext2);
      font-size: 12px;
    }
  }

  .item-department {
    flex-shrink: 0;
  }
}

.form-tip {
  margin: 4px 0 0 0;
  color: var(--subtext2);
  font-size: 12px;
}

// 响应式设计
@media (max-width: 768px) {
  .parser-manager {
    padding: 16px;
  }

  .top-bar {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;

    .left {
      flex-direction: column;
      width: 100%;

      .search-input {
        width: 100%;
      }
    }
  }

  .department-filter {
    .filter-tags {
      gap: 6px;
      
      .filter-tag {
        font-size: 12px;
        padding: 3px 8px;
      }
    }
  }

  .items-grid {
    grid-template-columns: 1fr;
  }
}

// 详情模态框样式
.detail-content {
  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--border);

    .detail-title {
      flex: 1;
      margin-right: 16px;

      h2 {
        margin: 0 0 8px 0;
        color: var(--text2);
        font-size: 20px;
        font-weight: 600;
      }

      .detail-meta {
        display: flex;
        gap: 8px;
      }
    }

    .detail-actions {
      flex-shrink: 0;
    }
  }

  .detail-section {
    margin-bottom: 24px;

    h3 {
      margin: 0 0 12px 0;
      color: var(--text2);
      font-size: 16px;
      font-weight: 600;
      padding-bottom: 8px;
      border-bottom: 1px solid var(--border);
    }

    .demo-content {
      background: var(--bg3);
      border-radius: 6px;
      padding: 16px;
      border-left: 4px solid var(--primary);

      .demo-text {
        color: var(--text2);
        font-size: 14px;
        line-height: 1.6;
        white-space: pre-wrap;
        word-break: break-word;
      }
    }

    .no-demo {
      text-align: center;
      padding: 20px;
      color: var(--subtext2);
      background: var(--bg3);
      border-radius: 6px;
      border: 1px dashed var(--border);
    }

    .content-preview {
      background: var(--bg3);
      border-radius: 6px;
      padding: 16px;
      border: 1px solid var(--border);
      max-height: 300px;
      overflow-y: auto;

      pre {
        margin: 0;
        color: var(--text2);
        font-size: 13px;
        line-height: 1.5;
        white-space: pre-wrap;
        word-break: break-word;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      }
    }
  }

  .detail-footer {
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid var(--border);

    .detail-info {
      display: flex;
      gap: 24px;
      color: var(--subtext2);
      font-size: 13px;

      span {
        display: flex;
        align-items: center;
        gap: 4px;
      }
    }
  }
}
</style>
